import { supabase } from './supabase';
import type { AppNotification, NotificationListOptions, NotificationSender } from '../types/notifications';

const EXCLUDED_NOTIFICATION_TYPES = new Set([
  'message',
  'direct_message',
  'dm',
  'kids_church_message',
  'kids_church_chat',
  'kids_church_dm',
]);

const normalizeReadStatus = (row: Record<string, any>) => {
  if (typeof row?.read === 'boolean') return row.read;
  if (typeof row?.is_read === 'boolean') return row.is_read;
  const readAt = row?.read_at ?? row?.readAt;
  return Boolean(readAt);
};

const normalizeNotificationRow = (row: Record<string, any>): AppNotification => ({
  id: typeof row.id === 'string' ? row.id : String(row.id ?? ''),
  type: typeof row.type === 'string' ? row.type : 'notification',
  title: typeof row.title === 'string' ? row.title : 'Notification',
  message: typeof row.message === 'string' ? row.message : '',
  created_at: typeof row.created_at === 'string' ? row.created_at : new Date(0).toISOString(),
  read: normalizeReadStatus(row),
  recipient_id: typeof row.recipient_id === 'string' ? row.recipient_id : null,
  sender_id: typeof row.sender_id === 'string' ? row.sender_id : null,
  sender_name: typeof row.sender_name === 'string' ? row.sender_name : null,
  data: typeof row.data === 'object' && row.data ? row.data : null,
  sender: row.sender ?? null,
});

const mergeNotifications = (rows: AppNotification[]) => {
  const merged = rows.reduce<Record<string, AppNotification>>((acc, row) => {
    const existing = acc[row.id];
    if (!existing) {
      acc[row.id] = row;
      return acc;
    }

    acc[row.id] = {
      ...existing,
      ...row,
      read: Boolean(existing.read || row.read),
      sender: row.sender ?? existing.sender ?? null,
    };
    return acc;
  }, {});

  return Object.values(merged)
    .filter((row) => !EXCLUDED_NOTIFICATION_TYPES.has(String(row.type || '').toLowerCase()))
    .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
};

const getSenderMap = async (senderIds: string[]) => {
  const uniqueSenderIds = Array.from(new Set(senderIds.filter(Boolean)));
  if (!uniqueSenderIds.length) {
    return new Map<string, NotificationSender>();
  }

  const { data, error } = await supabase
    .from('users')
    .select('id, full_name, email, avatar')
    .in('id', uniqueSenderIds);

  if (error || !Array.isArray(data)) {
    return new Map<string, NotificationSender>();
  }

  return new Map(
    data
      .filter((row) => typeof row?.id === 'string' && row.id.length > 0)
      .map((row) => [
        row.id as string,
        {
          id: row.id as string,
          full_name: typeof row.full_name === 'string' ? row.full_name : 'Unknown user',
          email: typeof row.email === 'string' ? row.email : null,
          avatar: typeof row.avatar === 'string' ? row.avatar : null,
        },
      ]),
  );
};

const attachSenders = async (rows: AppNotification[]) => {
  const senderMap = await getSenderMap(
    rows
      .map((row) => row.sender_id)
      .filter((senderId): senderId is string => typeof senderId === 'string' && senderId.length > 0),
  );

  if (!senderMap.size) {
    return rows;
  }

  return rows.map((row) => ({
    ...row,
    sender: row.sender ?? (row.sender_id ? senderMap.get(row.sender_id) ?? null : null),
  }));
};

const buildExcludedTypesCsv = () =>
  `(${Array.from(EXCLUDED_NOTIFICATION_TYPES)
    .map((type) => `"${type}"`)
    .join(',')})`;

export const notificationService = {
  async getNotifications(
    userId: string,
    { page = 1, limit = 100, signal }: NotificationListOptions = {},
  ): Promise<AppNotification[]> {
    const offset = (page - 1) * limit;

    const feedQuery = supabase
      .from('notification_feed')
      .select('*')
      .eq('recipient_id', userId)
      .order('created_at', { ascending: false })
      .range(offset, offset + limit - 1);
    const { data: feedRows, error: feedError } = await (signal ? feedQuery.abortSignal(signal) : feedQuery);

    if (!feedError) {
      return mergeNotifications(
        await attachSenders(((feedRows ?? []) as Record<string, any>[]).map(normalizeNotificationRow)),
      );
    }

    const legacyQuery = supabase
      .from('notifications')
      .select('*')
      .eq('recipient_id', userId)
      .order('created_at', { ascending: false })
      .range(offset, offset + limit - 1);
    const { data: legacyRows, error: legacyError } = await (signal ? legacyQuery.abortSignal(signal) : legacyQuery);

    if (legacyError) {
      throw new Error(feedError.message || legacyError.message || 'Failed to fetch notifications.');
    }

    return mergeNotifications(await attachSenders(((legacyRows ?? []) as Record<string, any>[]).map(normalizeNotificationRow)));
  },

  async getUnreadNotificationCount(userId: string): Promise<number> {
    const excludedTypesCsv = buildExcludedTypesCsv();
    const { count: feedCount, error: feedError } = await supabase
      .from('notification_feed')
      .select('id', { count: 'exact', head: true })
      .eq('recipient_id', userId)
      .or('read.eq.false,read.is.null')
      .not('type', 'in', excludedTypesCsv);

    if (!feedError) {
      return Number(feedCount ?? 0);
    }

    const { count: legacyCount, error: legacyError } = await supabase
      .from('notifications')
      .select('id', { count: 'exact', head: true })
      .eq('recipient_id', userId)
      .or('read.eq.false,read.is.null')
      .not('type', 'in', excludedTypesCsv);

    if (legacyError) {
      throw new Error(feedError.message || legacyError.message || 'Failed to fetch unread count.');
    }

    return Number(legacyCount ?? 0);
  },

  async markNotificationAsRead(notificationId: string) {
    const [feedResult, legacyResult] = await Promise.all([
      supabase.from('notification_feed').update({ read: true }).eq('id', notificationId).select('id'),
      supabase.from('notifications').update({ read: true }).eq('id', notificationId).select('id'),
    ]);

    if (feedResult.error && legacyResult.error) {
      throw new Error(feedResult.error.message || legacyResult.error.message || 'Failed to mark notification as read.');
    }
  },

  async markAllNotificationsAsRead(userId: string) {
    const [feedResult, legacyResult] = await Promise.all([
      supabase.from('notification_feed').update({ read: true }).eq('recipient_id', userId).select('id'),
      supabase.from('notifications').update({ read: true }).eq('recipient_id', userId).select('id'),
    ]);

    if (feedResult.error && legacyResult.error) {
      throw new Error(
        feedResult.error.message || legacyResult.error.message || 'Failed to mark all notifications as read.',
      );
    }
  },

  async clearAllNotifications(userId: string) {
    const [feedResult, legacyResult] = await Promise.all([
      supabase.from('notification_feed').delete().eq('recipient_id', userId),
      supabase.from('notifications').delete().eq('recipient_id', userId),
    ]);

    if (feedResult.error && legacyResult.error) {
      throw new Error(feedResult.error.message || legacyResult.error.message || 'Failed to clear notifications.');
    }
  },

  async deleteNotification(notificationId: string) {
    const [feedResult, legacyResult] = await Promise.all([
      supabase.from('notification_feed').delete().eq('id', notificationId),
      supabase.from('notifications').delete().eq('id', notificationId),
    ]);

    if (feedResult.error && legacyResult.error) {
      throw new Error(feedResult.error.message || legacyResult.error.message || 'Failed to delete notification.');
    }
  },
};
